import { Card, Skeleton, Typography } from 'antd';
import classNames from 'classnames';
import { memo, useMemo } from 'react';
import BasePagination from 'components/atoms/base-pagination';
import BaseEmpty from 'components/atoms/oneplatform/base-empty';

import AppointmentRow from './appointment-row';
import styles from './styles.module.scss';
import { sanitizeAppointments } from './utils';

import type { TTodayAppointmentsProps } from './types';

const { Text } = Typography;

const TodayAppointments = memo(
  ({
    title,
    count,
    appointments,
    emptyMessage,
    className,
    actionButtons,
    page = 1,
    pageSize = 6,
    total,
    onPageChange,
    isLoading,
  }: TTodayAppointmentsProps) => {
    const visibleAppointments = useMemo(
      () => sanitizeAppointments(appointments),
      [appointments],
    );

    const totalAppointments = useMemo(() => {
      if (typeof total === 'number' && total >= 0) return total;
      return visibleAppointments.length;
    }, [total, visibleAppointments.length]);

    const skeletonPlaceholders = useMemo(
      () => Array.from({ length: Math.max(pageSize, 3) }),
      [pageSize],
    );

    const badgeAriaLabel = useMemo(() => {
      if (typeof count !== 'number') return undefined;
      return title ? `${count} ${title}` : `${count} appointments`;
    }, [count, title]);

    const cardAriaLabel = title || 'Today appointments';

    return (
      <Card
        className={classNames(styles['appointments-card'], className)}
        variant="borderless"
        role="region"
        aria-label={cardAriaLabel}
      >
        <div className={styles.header}>
          {title || typeof count === 'number' ? (
            <>
              <div className={styles['title-group']}>
                {title ? <Text className={styles.title}>{title}</Text> : null}
                {typeof count === 'number' ? (
                  <span
                    className={styles.badge}
                    aria-label={badgeAriaLabel}
                    aria-live="polite"
                  >
                    {count}
                  </span>
                ) : null}
              </div>
              {visibleAppointments.length > 0 && (
                <BasePagination
                  page={page}
                  total={totalAppointments}
                  pageSize={pageSize}
                  align="end"
                  simple
                  showSizeChanger={false}
                  className={styles.pagination}
                  onChange={(nextPage) => onPageChange?.(nextPage)}
                />
              )}
            </>
          ) : null}
        </div>
        {isLoading && (
          <div className={styles.list} aria-live="polite">
            {skeletonPlaceholders.map((_, index) => (
              <Skeleton
                key={`today-appointments-skeleton-${index}`}
                data-testid="today-appointments-skeleton"
                active
                paragraph={{ rows: 1, width: '60%' }}
                title={false}
              />
            ))}
          </div>
        )}
        {!isLoading && visibleAppointments.length > 0 && (
          <div className={styles.list}>
            {visibleAppointments.map((appointment) => (
              <AppointmentRow
                key={appointment.id}
                appointment={appointment}
                actionButtons={actionButtons}
              />
            ))}
          </div>
        )}
        {!isLoading && visibleAppointments.length === 0 && (
          <BaseEmpty description={emptyMessage} />
        )}
      </Card>
    );
  },
);

TodayAppointments.displayName = 'TodayAppointments';

export default TodayAppointments;
